import { Database, Wifi } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { Hours } from '@obraplan/core/domain/shared/hours';
import {
  useContainer,
  usePermission,
  useSession,
  useSessionState,
} from '@/features/session/use-session';
import { DashboardPage } from './DashboardPage';
import { Figure } from '@/features/reports/Figure';
import { useLive } from '@/lib/use-live';

/**
 * A porta de entrada: com obra aberta, é o painel de indicadores; sem obra, mostra quantas
 * obras o perfil alcança e a jornada padrão.
 */
export function HomePage() {
  const container = useContainer();
  const session = useSession();
  const { activeProjectId } = useSessionState();
  const canSeeReports = usePermission(PERMISSION.REPORTS_VIEW);

  const projects = useLive(() => container.listAccessibleProjects.execute(session), [session]);
  const calendar = useLive(() => container.getDefaultWorkCalendar.execute(), []);

  // Total da semana pelo calendário — soma dos dias úteis, não jornada × 5 (RN-042).
  const weeklyHours =
    calendar === undefined
      ? undefined
      : Hours.ofQuarters(
          calendar.workingWeekdays.reduce(
            (total, weekday) =>
              total + (calendar.weekdayQuarterHours?.[weekday] ?? calendar.dailyQuarterHours),
            0,
          ),
        );

  if (activeProjectId !== undefined && canSeeReports) return <DashboardPage />;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Painel</h1>
        <p className="text-(--color-content-muted)">
          Olá, {session.userName}. Escolha uma obra acima para ver os indicadores dela.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Obras acessíveis</CardTitle>
            <CardDescription>Escopo do seu perfil</CardDescription>
          </CardHeader>
          <CardContent>
            <Figure value={projects === undefined ? '—' : String(projects.length)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Jornada padrão</CardTitle>
            <CardDescription>Definida no calendário, não no código</CardDescription>
          </CardHeader>
          <CardContent>
            {/* O número é o da semana, não o do dia: a jornada varia por dia para fechar as 44h. */}
            <Figure value={weeklyHours?.format() ?? '—'} />
            <p className="mt-2 text-sm text-(--color-content-muted)">
              {calendar?.name ?? 'Calendário não encontrado'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Funcionamento</CardTitle>
            <CardDescription>Este sistema roda num servidor</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Badge tone="info" icon={<Wifi aria-hidden="true" />}>
              Exige conexão com a internet
            </Badge>
            <Badge tone="ok" icon={<Database aria-hidden="true" />}>
              Backup é feito pelo servidor
            </Badge>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
